/**
 * Client-side fulfilment dates.
 *
 * The blocks also render these dates server-side so they are correct with JS
 * off, but Liquid runs on Shopify's clock against a page that may have been
 * cached for hours — long enough for "arrives Sep 6-11" to be a day stale.
 * This recomputes against the visitor's own clock on load.
 *
 * Offsets are calendar days and must match snippets/arrival-dates.liquid.
 * Each block wraps its dates in its own <product-arrival> so the offsets travel
 * with the block that owns them.
 */
class ProductArrival extends HTMLElement {
  connectedCallback() {
    const min = this.num('min-days', 7);
    const max = this.num('max-days', 12);
    const packed = this.num('packed-days', 1);
    const shipped = this.num('shipped-days', 2);

    /** Each target is a [from, to] offset pair; equal pairs render one date. */
    const spans = {
      today: [0, 0],
      packed: [packed, packed],
      shipped: [shipped, shipped],
      'ship-range': [packed, shipped],
      range: [min, max],
    };

    const now = new Date();

    for (const el of this.querySelectorAll('[data-arrival]')) {
      const span = spans[el.dataset.arrival];
      if (!span) continue;
      el.textContent = this.formatSpan(now, span[0], span[1]);
    }
  }

  num(attr, fallback) {
    const value = Number(this.getAttribute(attr));
    return Number.isFinite(value) ? value : fallback;
  }

  addDays(from, days) {
    const d = new Date(from.getTime());
    d.setDate(d.getDate() + days);
    return d;
  }

  format(date) {
    return new Intl.DateTimeFormat(this.getAttribute('locale') || undefined, {
      month: 'short',
      day: 'numeric',
    }).format(date);
  }

  /** Same month collapses to "Sep 6-11"; crossing one needs both names. */
  formatSpan(now, fromDays, toDays) {
    const from = this.addDays(now, fromDays);
    if (fromDays === toDays) return this.format(from);

    const to = this.addDays(now, toDays);
    const sameMonth = from.getMonth() === to.getMonth() && from.getFullYear() === to.getFullYear();
    return `${this.format(from)}-${sameMonth ? to.getDate() : this.format(to)}`;
  }
}

if (!customElements.get('product-arrival')) {
  customElements.define('product-arrival', ProductArrival);
}
